import PropTypes from "prop-types"
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ComingInV25 } from "@/components/common/ComingInV25";
import { cn } from "@/lib/utils";
import { useOnboardingStore } from "@/store/useOnboardingStore";
import { usePersonaStore } from "@/store/usePersonaStore";
import { voiceFingerprintSampleCount } from "@/lib/personaSamples";
import { useTeamStore, useAssistantName } from "@/store/useTeamStore";
import { StepWhatsApp, StepTelegram, StepLinkedIn, StepGmailCalendar, StepReddit, StepQuora } from "@/components/onboarding/OnboardingChannelSteps";
import { OnboardingStepBadge } from "@/components/onboarding/OnboardingStepBadge";
import { OnboardingStepErrorBoundary } from "@/components/onboarding/OnboardingStepErrorBoundary";
import { onboardingApi, personaApi } from "@/lib/api";
import { ONBOARDING_EXTENDED_STEPS_ENABLED, TEAM_INVITES_UI_ENABLED } from "@/lib/featureFlags";
import {
  getOnboardingLastStep,
  getOnboardingStepLabel,
  isOnboardingStepVisible,
  onboardingSlugFromStep,
  resolveOnboardingStep,
  resolveStoredOnboardingStep,
} from "@/lib/onboardingSteps";
import { openclawPairingMessage } from "@/lib/pairingErrors";
import { userFirstName } from "@/lib/userDisplay";
import { useAppStore } from "@/store/useAppStore";
import { Logo } from "@/components/common/Logo";
import {
  Sparkles, ArrowRight, ArrowLeft,
  Clock, CheckCircle2, Mic, Bell, Smartphone, ShieldCheck, X, UserPlus, Upload, FileSpreadsheet, Trash2,
  MessageCircle, Linkedin, Mail, Loader2, AlertCircle,
} from "lucide-react";

const LAST_ONBOARDING_STEP = getOnboardingLastStep();

function isOpenclawGatewayReady(openclaw) {
  return Boolean(openclaw?.gateway_reachable);
}

function isOpenclawPairingPending(openclaw, errMsg = "") {
  const err = String(openclaw?.error || errMsg || "").toLowerCase();
  return Boolean(
    openclaw?.pairing_required
    || err.includes("pairing")
    || err.includes("not approved"),
  );
}

isOpenclawPairingPending.propTypes = {
  step: PropTypes.string,
};

const OPENCLAW_SYNC_ERROR =
  "Unable to synchronize the agent with OpenClaw. Please check the gateway connection and try again.";

async function fetchOpenclawStatus() {
  try {
    const status = await onboardingApi.openclawStatus();
    return status ?? null;
  } catch {
    return null;
  }
}

function toastProvisionResult(r, agentName) {
  const description = r.openclaw_agent_id ? `Agent ID: ${r.openclaw_agent_id}` : undefined;
  if (r?.created) {
    toast.success(`${r.agent_name || agentName} created on OpenClaw`, { description });
  } else if (r?.linked) {
    toast.success(`${r.agent_name || agentName} linked to OpenClaw`, { description });
  } else if (r?.reused) {
    toast.message(`${r.agent_name || agentName} already configured — continuing`, { description });
  } else {
    toast.success(`${r.agent_name || agentName} updated on OpenClaw`, { description });
  }
}

function handlePersonaProvisionError(e, agentName) {
  const errMsg = String(e?.message || "").toLowerCase();
  if (e?.code === "agent_name_conflict" || e?.status === 409) {
    toast.error(e?.message || "This agent name is already in use on the gateway", {
      description: "Choose a different name, or keep the name shown in the OpenClaw status line above.",
    });
    return;
  }
  if (e?.status === 503 || isOpenclawPairingPending(null, errMsg)) {
    toast.error(OPENCLAW_SYNC_ERROR, {
      description: e?.message || openclawPairingMessage({ error: e?.message }),
      duration: 9000,
    });
    return;
  }
  toast.error(e?.message || OPENCLAW_SYNC_ERROR);
}

async function continuePersonaStep({ agentName, pronouns }) {
  const openclawStatus = await fetchOpenclawStatus();
  if (!isOpenclawGatewayReady(openclawStatus)) {
    const err = new Error(openclawPairingMessage(openclawStatus) || OPENCLAW_SYNC_ERROR);
    err.status = 503;
    err.code = "openclaw_unreachable";
    throw err;
  }

  // Always call OpenClaw sync — never skip based on stale DB mapping alone.
  // A deleted gateway agent must be recreated / rediscovered here.
  const r = await onboardingApi.provisionAgent({
    agent_name: agentName,
    pronouns: pronouns || undefined,
  });
  if (!r?.openclaw_agent_id || r?.gateway_patch_ok === false) {
    const err = new Error(OPENCLAW_SYNC_ERROR);
    err.status = 503;
    err.code = "agent_provision_failed";
    throw err;
  }

  // Persist Sellix persona after OpenClaw confirms (avoid false success).
  await personaApi.update({
    assistant_name: agentName,
    pronouns: pronouns || undefined,
  });
  toastProvisionResult(r, agentName);
  try {
    await onboardingApi.updateProgress({ step: "agent" });
  } catch {
    /* best-effort */
  }
}

const ONBOARDING_PROGRESS_KEYS = ["p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8", "p9", "p10", "p11", "p12"];

function ProgressBar({ step }) {
  const visible = getOnboardingStepLabel(step);
  const segments = visible?.total ?? 1;
  const activeIndex = (visible?.index ?? 1) - 1;
  return (
    <div className="flex items-center gap-1.5">
      {ONBOARDING_PROGRESS_KEYS.slice(0, segments).map((id, i) => {
        let barClass = "bg-slate-200";
        if (i < activeIndex) barClass = "bg-emerald-500";
        else if (i === activeIndex) barClass = "bg-indigo-600";
        return (
          <div key={id} className={cn("h-1 flex-1 rounded-full transition-colors", barClass)} />
        );
      })}
    </div>
  );
}
ProgressBar.propTypes = {
  step: PropTypes.number.isRequired,
};

function syncAuthedOnboarding({ status, stepParam, hydrateFromServer, setStep, navigate }) {
  hydrateFromServer(status);
  if (status.onboarding_completed) {
    navigate("/today", { replace: true });
    return;
  }
  const resumeStep = resolveStoredOnboardingStep(status.onboarding_step);
  setStep(resumeStep);
  const urlIsWelcome = !stepParam || stepParam === "welcome";
  const urlStep = Number(stepParam);
  const urlMismatch = urlIsWelcome || Number.isNaN(urlStep) || urlStep !== resumeStep;
  if (urlMismatch) {
    navigate(`/onboarding/${resumeStep}`, { replace: true });
  }
}

function syncUnauthedOnboarding({ completed, navigate }) {
  if (completed) {
    navigate("/today", { replace: true });
  }
}

function syncOnboardingError({ completed, currentStep, stepParam, navigate }) {
  if (completed) {
    navigate("/today", { replace: true });
    return;
  }
  const resumeStep = Math.max(1, Number(currentStep) || 1);
  if (!stepParam || stepParam === "welcome") {
    navigate(`/onboarding/${resumeStep}`, { replace: true });
  }
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { step: stepParam } = useParams();
  const { currentStep, setStep, markCompleted, hydrateFromServer, completed } = useOnboardingStore();
  const { name, pronouns, setPronouns } = usePersonaStore();
  const { isAuthed, authReady } = useAppStore();
  const step = Math.max(1, Math.min(LAST_ONBOARDING_STEP, stepParam ? Number(stepParam) || 1 : currentStep));
  const stepLabel = getOnboardingStepLabel(step);
  const [continueBusy, setContinueBusy] = useState(false);
  const [gateReady, setGateReady] = useState(false);
  const continueInFlight = useRef(false);
  const gatedOnce = useRef(false);

  // Completed → /today; in progress → land on DB-stored step.
  useEffect(() => {
    if (!authReady || gatedOnce.current) return undefined;
    let cancelled = false;

    const syncFromServer = async () => {
      try {
        if (isAuthed) {
          const status = await onboardingApi.status();
          if (cancelled) return;
          syncAuthedOnboarding({ status, stepParam, hydrateFromServer, setStep, navigate });
        } else {
          syncUnauthedOnboarding({ completed, navigate });
        }
      } catch {
        if (cancelled) return;
        syncOnboardingError({ completed, currentStep, stepParam, navigate });
      } finally {
        if (!cancelled) {
          gatedOnce.current = true;
          setGateReady(true);
        }
      }
    };

    syncFromServer();
    return () => { cancelled = true; };
  }, [
    authReady, isAuthed, completed, currentStep, hydrateFromServer, navigate,
    setStep, stepParam,
  ]);

  useEffect(() => {
    if (stepParam && stepParam !== "welcome") setStep(Number(stepParam) || 1);
  }, [stepParam, setStep]);

  useEffect(() => {
    personaApi.get()
      .then((p) => {
        if (p?.pronouns) setPronouns(p.pronouns);
      })
      .catch(() => { /* fresh tenant */ });
  }, [setPronouns]);

  useEffect(() => {
    if (!gateReady) return;
    if (!isOnboardingStepVisible(step)) {
      const target = resolveOnboardingStep(step, step > 1 ? step - 1 : step + 1);
      setStep(target);
      navigate(`/onboarding/${target}`, { replace: true });
    }
  }, [gateReady, step, navigate, setStep]);

  useEffect(() => {
    if (!gateReady) return;
    const n = Number(stepParam);
    if (stepParam && stepParam !== "welcome" && !Number.isNaN(n) && n > LAST_ONBOARDING_STEP) {
      setStep(LAST_ONBOARDING_STEP);
      navigate(`/onboarding/${LAST_ONBOARDING_STEP}`, { replace: true });
    }
  }, [gateReady, stepParam, navigate, setStep]);

  if (!gateReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="onboarding-gating">
        <Loader2 className="animate-spin text-indigo-600" size={28} aria-hidden />
      </div>
    );
  }
  const go = async (n) => {
    const target = resolveOnboardingStep(n, step);
    const slug = onboardingSlugFromStep(target);
    setStep(target);
    if (slug) {
      try {
        await onboardingApi.updateProgress({ step: slug });
      } catch {
        /* best-effort — still move UI */
      }
    }
    navigate(`/onboarding/${target}`);
  };
  const finish = async () => {
    if (continueInFlight.current) return;
    continueInFlight.current = true;
    setContinueBusy(true);
    try {
      const payload = await onboardingApi.complete();
      hydrateFromServer(payload);
      markCompleted(payload?.onboarding_completed_at);
      try {
        confetti({
          particleCount: 120, spread: 75, startVelocity: 40, origin: { y: 0.5 },
          colors: ["#4F46E5", "#8B5CF6", "#10B981", "#F59E0B"], scalar: 1.0,
        });
      } catch (_) { /* best */ }
      navigate("/today");
    } catch (e) {
      toast.error(e?.message || "Could not finish setup. Please try again.");
    } finally {
      continueInFlight.current = false;
      setContinueBusy(false);
    }
  };
  const next = () => (step === LAST_ONBOARDING_STEP ? finish() : go(step + 1));
  const back = () => step > 1 && go(step - 1);
  const skipAll = () => {
    // Do NOT mark onboarding complete — Finish setup is the only gate.
    toast.message(
      "Onboarding has not been completed. You can start or resume the onboarding process at any time from Settings → Connections → Start Onboarding.",
      { duration: Infinity },
    );
    navigate("/today");
  };

  const handleContinue = async () => {
    if (continueInFlight.current) return;
    if (step !== 2) {
      next();
      return;
    }

    const agentName = (name || "").trim();
    if (!agentName) {
      toast.error("Enter a name for your agent");
      return;
    }

    continueInFlight.current = true;
    setContinueBusy(true);
    try {
      try {
        await continuePersonaStep({ agentName, pronouns });
        go(step + 1);
      } catch (e) {
        handlePersonaProvisionError(e, agentName);
      }
    } finally {
      continueInFlight.current = false;
      setContinueBusy(false);
    }
  };

  return (
    <div className="min-h-dvh w-full max-w-[100vw] overflow-x-hidden bg-gradient-to-br from-indigo-50/40 via-white to-violet-50/30 flex items-start sm:items-center justify-center p-4 sm:p-6 py-6 sm:py-10">
      <div className="w-full max-w-[640px] min-w-0">
        <div className="flex items-start sm:items-center justify-between gap-3 mb-4">
          <div className="inline-flex items-center gap-2 text-[12px] sm:text-[12.5px] text-slate-600 min-w-0 flex-wrap">
            <Logo size={20} />
            <span className="text-slate-400">·</span>
            <span>Setup</span>
            <span className="text-slate-400">·</span>
            <span className="tabular-nums text-slate-500">{stepLabel ? `${stepLabel.index} of ${stepLabel.total}` : step}</span>
          </div>
          <button type="button" onClick={skipAll} className="text-[11.5px] text-slate-400 hover:text-slate-600 font-medium min-h-10 sm:min-h-0 shrink-0 px-1" data-testid="onboarding-skip-all">Skip all</button>
        </div>
        <ProgressBar step={step} />
        <div className="mt-4 sm:mt-5 rounded-2xl border border-slate-200 bg-white shadow-sm p-4 sm:p-6 md:p-8 min-w-0" data-testid={`onboarding-step-${step}`}>
          <OnboardingStepErrorBoundary step={step} onBack={step > 1 ? back : undefined}>
            {step === 1 && <StepWelcome onNext={next} />}
            {step === 2 && <StepNaming />}
            {step === 3 && TEAM_INVITES_UI_ENABLED && <StepInviteTeam />}
            {step === 4 && <StepWhatsApp />}
            {step === 5 && <StepTelegram />}
            {step === 6 && <StepGmailCalendar />}
            {step === 7 && <StepLinkedIn />}
            {ONBOARDING_EXTENDED_STEPS_ENABLED && step === 8 && <StepReddit />}
            {ONBOARDING_EXTENDED_STEPS_ENABLED && step === 9 && <StepQuora />}
            {ONBOARDING_EXTENDED_STEPS_ENABLED && step === 10 && <StepVoice />}
            {ONBOARDING_EXTENDED_STEPS_ENABLED && step === 11 && <StepImportData />}
            {ONBOARDING_EXTENDED_STEPS_ENABLED && step === 12 && <StepBriefing />}
          </OnboardingStepErrorBoundary>
        </div>
        {step > 1 && (
          <div className="mt-3 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2">
            <Button variant="ghost" size="sm" className="h-10 sm:h-8 text-[12px] w-full sm:w-auto" onClick={back} data-testid="onboarding-back"><ArrowLeft size={12} /> Back</Button>
            <Button size="sm" className="h-10 sm:h-9 text-[12.5px] px-4 w-full sm:w-auto" onClick={handleContinue} disabled={continueBusy} data-testid="onboarding-next">
              {continueBusy ? (
                <><Loader2 size={12} className="animate-spin" /> Provisioning…</>
              ) : (
                <>{step === LAST_ONBOARDING_STEP ? "Finish setup" : "Continue"} {step === LAST_ONBOARDING_STEP ? <Sparkles size={12} /> : <ArrowRight size={12} />}</>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepWelcome({ onNext }) {
  const persona = usePersonaStore();
  return (
    <div className="text-center min-w-0">
      <div className="inline-flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md ring-8 ring-indigo-50">
        <Sparkles size={32} strokeWidth={2.25} />
      </div>
      <div className="mt-5 text-[22px] sm:text-[28px] font-semibold tracking-tight text-slate-900 leading-tight">Meet your personal AI SDR.</div>
      <p className="mt-3 text-[13px] sm:text-[14px] text-slate-600 max-w-md mx-auto leading-relaxed">
        Sellyx is a personal AI SDR for founders. {persona.name} works on your WhatsApp, LinkedIn, Gmail — <span className="font-semibold text-slate-800">as you</span>.
        Let&apos;s get your AI set up in 5 minutes.
      </p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-2 text-left">
        {[
          { Icon: MessageCircle, title: "Replies 24/7 as you", body: "On your own number — never a business API." },
          { Icon: Linkedin, title: "Works on your channels", body: "No APIs · no platform setup · just pair." },
          { Icon: ShieldCheck, title: "Your data, your instance", body: "Sessions live on your Sellyx · never our cloud." },
        ].map((c) => (
          <div key={c.title} className="rounded-lg border border-slate-200 p-3">
            <c.Icon size={14} className="text-indigo-600" />
            <div className="mt-1.5 text-[12.5px] font-semibold text-slate-900">{c.title}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{c.body}</div>
          </div>
        ))}
      </div>
      <Button size="lg" className="mt-6 h-11 sm:h-10 px-5 w-full sm:w-auto" onClick={onNext} data-testid="onboarding-start">
        Let&apos;s go <ArrowRight size={14} />
      </Button>
    </div>
  );
}

StepWelcome.propTypes = {
  onNext: PropTypes.func,
};

function StepInviteTeam() {
  const maya = useAssistantName();
  const addInvitedMember = useTeamStore((s) => s.addInvitedMember);
  const [rows, setRows] = useState([
    { id: "invite-cofounder", email: "", role: "Cofounder" },
    { id: "invite-ea", email: "", role: "EA" },
    { id: "invite-content", email: "", role: "Content" },
    { id: "invite-creative", email: "", role: "Creative" },
  ]);
  const setRow = (i, patch) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const sendAll = () => {
    const valid = rows.filter((r) => r.email.trim().length > 3 && r.email.includes("@"));
    valid.forEach((r) => addInvitedMember(r));
    toast.success(`${valid.length || 0} invite${valid.length === 1 ? "" : "s"} sent`);
  };
  return (
    <div>
      <OnboardingStepBadge stepId={3} />
      <div className="mt-1 text-[18px] sm:text-[22px] font-semibold tracking-tight text-slate-900">Invite your team</div>
      <p className="text-[13px] text-slate-600 mt-1">Up to 5 collaborators share this instance. {maya} routes the right thread to the right teammate automatically.</p>
      <div className="mt-5 space-y-2">
        {rows.map((r, i) => (
          <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-2.5 flex flex-col sm:flex-row sm:items-center gap-2 min-w-0" data-testid={`onboarding-invite-row-${i}`}>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0"><UserPlus size={14} /></div>
              <Input value={r.email} onChange={(e) => setRow(i, { email: e.target.value })} placeholder="teammate@company.com" className="h-10 sm:h-9 flex-1 min-w-0 text-[12.5px]" data-testid={`onboarding-invite-email-${i}`} />
            </div>
            <select value={r.role} onChange={(e) => setRow(i, { role: e.target.value })} className="h-10 sm:h-9 rounded-md border border-slate-200 px-2 text-[12px] w-full sm:w-auto" data-testid={`onboarding-invite-role-${i}`}>
              <option>Cofounder</option><option>EA</option><option>Content</option><option>Creative</option><option>Other</option>
            </select>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-[11px] text-slate-400">Skip — you can invite teammates later from Settings → Team.</span>
        <Button size="sm" variant="outline" className="h-9 sm:h-8 text-[11.5px] w-full sm:w-auto" onClick={sendAll} data-testid="onboarding-send-invites">Send invites</Button>
      </div>
    </div>
  );
}

function StepImportData() {
  const maya = useAssistantName();
  const [imported, setImported] = useState({ csv: false, gmail: false, linkedin: false });
  const csvInputRef = useRef(null);

  const importCsvFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const rows = text.split(/\r?\n/).filter((l) => l.trim()).length - 1;
      const count = Math.max(0, rows);
      setImported((p) => ({ ...p, csv: true }));
      toast.success(`Imported ${count} contact${count === 1 ? "" : "s"} from your CSV`);
    };
    reader.onerror = () => toast.error("Couldn't read that file");
    reader.readAsText(file);
  };

  const cards = [
    { id: "csv", Icon: FileSpreadsheet, title: "Upload a CSV", body: "Drop your contact export from any CRM.", action: "Browse files", count: 412, label: "your CSV" },
    { id: "gmail", Icon: Mail, title: "Connect Gmail contacts", body: "We'll pull people you've emailed recently.", action: "Connect Gmail", count: 218, label: "Gmail" },
    { id: "linkedin", Icon: Linkedin, title: "LinkedIn export", body: "Upload your LinkedIn data export ZIP.", action: "Upload export", count: 564, label: "LinkedIn" },
  ];
  return (
    <div>
      <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { importCsvFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="onboarding-csv-input" />
      <OnboardingStepBadge stepId={11} />
      <div className="mt-1 text-[18px] sm:text-[22px] font-semibold tracking-tight text-slate-900">Import your data</div>
      <p className="text-[13px] text-slate-600 mt-1">Bring your existing contacts in so {maya} knows who you already talk to.</p>
      <div className="mt-5 grid grid-cols-1 gap-2.5">
        {cards.map((c) => (
          <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col sm:flex-row sm:items-center gap-3 min-w-0" data-testid={`onboarding-import-${c.id}`}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0"><c.Icon size={16} /></div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-900">{c.title}</div>
                <div className="text-[11.5px] text-slate-500">{c.body}</div>
              </div>
            </div>
            {imported[c.id] ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 text-[11.5px] font-medium"><CheckCircle2 size={12} /> Imported</span>
            ) : (
              <Button size="sm" variant="outline" className="h-9 sm:h-8 text-[11.5px] w-full sm:w-auto shrink-0" onClick={() => {
                if (c.id === "csv") csvInputRef.current?.click();
                else if (c.id === "gmail") toast.message("Connect Gmail in Step 6", { description: "Use Continue to reach Gmail setup." });
                else toast.message("LinkedIn import is coming soon");
              }} data-testid={`onboarding-import-btn-${c.id}`}>
                <Upload size={11} /> {c.action}
              </Button>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4 text-center">
        <span className="text-[11px] text-slate-400">Skip — {maya} will pick up contacts as you have conversations.</span>
      </div>
    </div>
  );
}

function OpenclawStatusBanner({ statusLoading, openclaw }) {
  if (statusLoading) {
    return (
      <span className="inline-flex items-center gap-1.5 text-slate-500">
        <Loader2 size={12} className="animate-spin" /> Checking OpenClaw gateway…
      </span>
    );
  }
  if (isOpenclawGatewayReady(openclaw)) {
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-700">
        <CheckCircle2 size={12} /> OpenClaw connected
        {openclaw.agent_provisioned && openclaw.openclaw_agent_id && (
          <span className="text-slate-500">· agent <span className="font-mono">{openclaw.openclaw_agent_id}</span></span>
        )}
        {openclaw.gateway_version && (
          <span className="text-slate-500">· v{openclaw.gateway_version}</span>
        )}
      </span>
    );
  }
  if (openclaw?.configured || openclaw?.endpoint_status === "paired") {
    return (
      <span className="inline-flex items-start gap-1.5 text-amber-800">
        <AlertCircle size={12} className="shrink-0 mt-0.5" />
        <span>
          {openclawPairingMessage(openclaw)}
          {" "}
          <span className="text-slate-600">You can still continue — finish OpenClaw approval later in Settings.</span>
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-start gap-1.5 text-amber-800">
      <AlertCircle size={12} className="shrink-0 mt-0.5" />
      <span>{openclaw?.error || "OpenClaw gateway not reachable — check backend/.env (SELLIX_OPENCLAW_LOCAL_WS_URL, SELLIX_OPENCLAW_LOCAL_GATEWAY_TOKEN)"}</span>
    </span>
  );
}

OpenclawStatusBanner.propTypes = {
  statusLoading: PropTypes.bool,
  openclaw: PropTypes.object,
};

function AvatarPreview({ avatarPreviewUrl, avatarKind, name }) {
  if (avatarPreviewUrl) {
    return <img src={avatarPreviewUrl} alt="" className="h-full w-full object-cover" />;
  }
  if (avatarKind === "uploaded") {
    return <span className="text-[20px] font-semibold">{name[0]}</span>;
  }
  return <Sparkles size={26} strokeWidth={2.25} />;
}

AvatarPreview.propTypes = {
  avatarPreviewUrl: PropTypes.string,
  avatarKind: PropTypes.string,
  name: PropTypes.string,
};

function StepNaming() {
  const { name, tagline, pronouns, avatarKind, avatarPreviewUrl, setName, setTagline, setPronouns, setAvatarFromFile } = usePersonaStore();
  const { auth } = useAppStore();
  const ownerFirstName = userFirstName(auth?.user);
  const [draft, setDraft] = useState(name);
  const [openclaw, setOpenclaw] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const avatarInputRef = useRef(null);
  const nameEditedRef = useRef(false);

  useEffect(() => {
    if (!nameEditedRef.current) setDraft(name);
  }, [name]);

  const onAvatarSelected = (file) => {
    if (!file) return;
    const result = setAvatarFromFile(file);
    if (result?.ok === false) {
      toast.error(result.error || "Couldn't use that image");
      return;
    }
    toast.success("Photo updated");
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await onboardingApi.openclawStatus();
        if (!cancelled) setOpenclaw(s);
      } catch (e) {
        if (!cancelled) setOpenclaw({ error: e?.message || "Couldn't reach OpenClaw" });
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const renderOpenclawStatus = () => {
    if (statusLoading) {
      return <span className="inline-flex items-center gap-1.5 text-slate-500"><Loader2 size={12} className="animate-spin" /> Checking OpenClaw gateway…</span>;
    }
    if (isOpenclawGatewayReady(openclaw)) {
      return (
        <div className="space-y-1.5">
          <span className="inline-flex items-center gap-1.5 text-emerald-700">
            <CheckCircle2 size={12} /> OpenClaw connected
            {openclaw.agent_provisioned && openclaw.openclaw_agent_id && (
              <span className="text-slate-500">· agent <span className="font-mono">{openclaw.openclaw_agent_id}</span></span>
            )}
            {openclaw.gateway_version && (
              <span className="text-slate-500">· v{openclaw.gateway_version}</span>
            )}
          </span>
          {openclaw.endpoint_env_mismatch && openclaw.warning && (
            <span className="inline-flex items-start gap-1.5 text-amber-800">
              <AlertCircle size={12} className="shrink-0 mt-0.5" />
              <span>{openclaw.warning}</span>
            </span>
          )}
        </div>
      );
    }
    if (openclaw?.configured || openclaw?.endpoint_status === "paired") {
      return (
        <span className="inline-flex items-start gap-1.5 text-amber-800">
          <AlertCircle size={12} className="shrink-0 mt-0.5" />
          <span>
            {openclawPairingMessage(openclaw)}
            {" "}
            <span className="text-slate-600">Approve the Sellix device in OpenClaw before creating an agent — Sellix will not report success until the gateway confirms.</span>
          </span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-start gap-1.5 text-amber-800">
        <AlertCircle size={12} className="shrink-0 mt-0.5" />
        <span>{openclaw?.error || "OpenClaw gateway not reachable — check backend/.env (SELLIX_OPENCLAW_LOCAL_WS_URL, SELLIX_OPENCLAW_LOCAL_GATEWAY_TOKEN)"}</span>
      </span>
    );
  };

  return (
    <div>
      <OnboardingStepBadge stepId={2} />
      <div className="mt-1 text-[18px] sm:text-[22px] font-semibold tracking-tight text-slate-900">What should we call your AI?</div>
      <p className="text-[13px] text-slate-600 mt-1">This name becomes your real OpenClaw agent. Most founders keep &ldquo;Maya&rdquo; — you can rename anytime.</p>

      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-[11.5px] break-words"
           data-testid="onboarding-openclaw-status">
        {renderOpenclawStatus()}
      </div>
      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
          className="hidden"
          data-testid="onboarding-avatar-input"
          onChange={(e) => {
            onAvatarSelected(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => avatarInputRef.current?.click()}
          className="group relative shrink-0"
          data-testid="onboarding-avatar"
          aria-label="Upload photo"
        >
          <div className="h-20 w-20 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center shadow-md ring-4 ring-white overflow-hidden">
            <AvatarPreview avatarPreviewUrl={avatarPreviewUrl} avatarKind={avatarKind} name={name} />
          </div>
          <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 text-white text-[9px] font-medium flex items-center justify-center text-center px-1">
            Upload Photo
          </div>
        </button>
        <div className="flex-1 min-w-0 sm:min-w-[220px] space-y-2">
          <Input
            value={draft}
            onChange={(e) => {
              const v = e.target.value;
              nameEditedRef.current = true;
              setDraft(v);
              setName(v);
            }}
            onBlur={() => {
              nameEditedRef.current = true;
              setName(draft);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                nameEditedRef.current = true;
                setName(draft);
              }
            }}
            placeholder="e.g., Nova, Sage, Iris"
            className="text-[16px] h-10 font-semibold"
            data-testid="onboarding-name-input"
          />
          <Input
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="Your AI SDR"
            className="text-[12.5px] h-8"
            data-testid="onboarding-tagline-input"
          />
          <div className="flex items-center gap-1.5 flex-wrap">
            {["she/her", "he/him", "they/them"].map((p) => (
              <button key={p} type="button" onClick={() => setPronouns(p)} className={cn("text-[10.5px] rounded-full px-2 py-0.5 ring-1 font-medium transition-colors", pronouns === p ? "bg-indigo-50 text-indigo-700 ring-indigo-200" : "bg-white text-slate-600 ring-slate-200")}>{p}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-5 rounded-lg bg-indigo-50/40 ring-1 ring-indigo-200 px-3 py-2.5 text-[12.5px] text-slate-700 italic">
        &ldquo;Hello, I&apos;m <span className="font-semibold not-italic text-slate-900">{draft || name || "Maya"}</span>. I work with {ownerFirstName}.&rdquo;
      </div>
    </div>
  );
}

const REC_WAVEFORM = [
  { id: "rw-a", height: 3 },
  { id: "rw-b", height: 6 },
  { id: "rw-c", height: 4 },
  { id: "rw-d", height: 8 },
  { id: "rw-e", height: 5 },
  { id: "rw-f", height: 7 },
  { id: "rw-g", height: 3 },
  { id: "rw-h", height: 6 },
  { id: "rw-i", height: 4 },
];

function StepVoice() {
  const { name, samples, setSample } = usePersonaStore();
  const celebratedRef = useRef(false);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  const sampleCount = voiceFingerprintSampleCount(samples);

  const discardRecording = () => {
    setSample("audioSampleName", null);
    setRecording(false);
    setRecSeconds(0);
    toast.message("Voice sample removed");
  };

  const startReRecord = () => {
    setSample("audioSampleName", null);
    setRecSeconds(0);
    setRecording(true);
  };
  useEffect(() => {
    if (sampleCount < 3 || celebratedRef.current) return;
    const k = "sellix.persona.voiceFingerprintCelebrated";
    if (localStorage.getItem(k)) return;
    localStorage.setItem(k, "1");
    celebratedRef.current = true;
    toast.success("Strong voice fingerprint locked in.", { description: `${name}'s outputs just got measurably more 'you'.`, duration: 5000 });
  }, [sampleCount, name]);

  const stopRecording = () => {
    setRecording(false);
    setSample("audioSampleName", `recording-${recSeconds}s.m4a`);
    toast.success(`${name} detected: warm, direct, founder-friendly.`);
    setRecSeconds(0);
  };

  const renderAudioSection = () => {
    if (samples.audioSampleName) {
      return (
        <div className="rounded-md ring-1 ring-emerald-200 bg-emerald-50/40 px-3 py-2 flex items-center gap-2 flex-wrap">
          <span className="text-[11.5px] text-emerald-800 inline-flex items-center gap-2">
            <CheckCircle2 size={12} /> Saved · {samples.audioSampleName}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-[10.5px]"
              onClick={startReRecord}
              data-testid="onboarding-rec-rerecord"
            >
              <Mic size={11} /> Re-record
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-[10.5px] text-rose-700 hover:text-rose-800 hover:bg-rose-50"
              onClick={discardRecording}
              data-testid="onboarding-rec-discard"
            >
              <Trash2 size={11} /> Discard
            </Button>
          </div>
        </div>
      );
    }
    if (recording) {
      return (
        <div className="rounded-md ring-1 ring-rose-200 bg-rose-50/40 px-3 py-2 flex flex-wrap items-center gap-3">
          <span className="relative h-2.5 w-2.5 shrink-0">
            <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-70" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" />
          </span>
          <span className="text-[12px] font-mono text-rose-700 tabular-nums">
            {String(Math.floor(recSeconds / 60)).padStart(2, "0")}:
            {String(recSeconds % 60).padStart(2, "0")}
          </span>
          <div className="flex items-end gap-0.5 h-4 ml-1">
            {REC_WAVEFORM.map((bar, i) => (
              <div
                key={bar.id}
                className="w-0.5 bg-rose-400 rounded-sm animate-pulse"
                style={{ height: `${bar.height * 2}px`, animationDelay: `${i * 100}ms` }}
              />
            ))}
          </div>
          <Button
            size="sm"
            className="h-9 sm:h-7 text-[10.5px] w-full sm:w-auto sm:ml-auto"
            onClick={stopRecording}
            data-testid="onboarding-rec-stop"
          >
            Stop &amp; save
          </Button>
        </div>
      );
    }
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-9 text-[11.5px]"
        onClick={() => setRecording(true)}
        data-testid="onboarding-rec-start"
      >
        <Mic size={12} /> Start recording
      </Button>
    );
  };

  const fingerprintPct = Math.round((sampleCount / 3) * 100);

  return (
    <div>
      <OnboardingStepBadge stepId={10} />
      <div className="mt-1 text-[18px] sm:text-[22px] font-semibold tracking-tight text-slate-900">Help {name} sound like you</div>
      <p className="text-[13px] text-slate-600 mt-1">The more you share, the better your AI writes. Most founders only need 3 samples.</p>

      <div className="mt-4 flex items-center justify-between text-[11px] mb-1.5">
        <span className="uppercase tracking-wider text-slate-500 font-semibold">Voice fingerprint</span>
        <span className="text-indigo-700 tabular-nums">{fingerprintPct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={cn("h-full transition-all duration-500", sampleCount >= 3 ? "bg-gradient-to-r from-emerald-400 to-emerald-600" : "bg-gradient-to-r from-indigo-400 to-violet-500")} style={{ width: `${fingerprintPct}%` }} />
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label htmlFor="onboarding-writing" className="text-[11.5px] font-semibold text-slate-700 mb-1 block">Drop in your writing samples</label>
          <Textarea id="onboarding-writing" rows={3} value={samples.writingSamples} onChange={(e) => setSample("writingSamples", e.target.value)} placeholder="Paste 3-5 of your best DMs or LinkedIn posts…" className="text-[12px] resize-none" data-testid="onboarding-writing" />
        </div>
        <div>
          <fieldset>
          <legend className="text-[11.5px] font-semibold text-slate-700 mb-1 block">Record a voice clip (~30-60s)</legend>
          {renderAudioSection()}
          </fieldset>
        </div>
        <div>
          <label htmlFor="onboarding-longform" className="text-[11.5px] font-semibold text-slate-700 mb-1 block">Paste a long-form essay or blog post</label>
          <Textarea id="onboarding-longform" rows={3} value={samples.longFormSample} onChange={(e) => setSample("longFormSample", e.target.value)} placeholder="Paste your founder essay or your most-read post…" className="text-[12px] resize-none" data-testid="onboarding-longform" />
        </div>
      </div>
      <div className="mt-3 text-center">
        <span className="text-[11px] text-slate-400">Skip — {name} will learn over time.</span>
      </div>
    </div>
  );
}

function StepBriefing() {
  const { data, setData } = useOnboardingStore();
  const { name } = usePersonaStore();
  const setTime = (t) => setData({ briefingTime: t });
  const toggleMethod = (k) => setData({ briefingMethods: { ...data.briefingMethods, [k]: !data.briefingMethods[k] } });
  const formatted = (() => {
    const [h, m] = data.briefingTime.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hh = ((h + 11) % 12 + 1);
    return `${hh}:${String(m).padStart(2, "0")} ${period}`;
  })();
  const METHOD_LABELS = { push: "Push", email: "Email", whatsapp: "WhatsApp" };
  const activeMethods = Object.entries(data.briefingMethods).filter(([, v]) => v).map(([k]) => k);
  const methodLabel = activeMethods.length === 0
    ? "your phone"
    : activeMethods.map((m) => METHOD_LABELS[m] || "WhatsApp").join(" + ");

  return (
    <div>
      <OnboardingStepBadge stepId={12} />
      <div className="mt-1 text-[18px] sm:text-[22px] font-semibold tracking-tight text-slate-900">When should {name} brief you?</div>
      <p className="text-[13px] text-slate-600 mt-1">A 30-second morning brief so you walk into your day knowing exactly what happened.</p>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={13} className="text-slate-500" />
          <span className="text-[11.5px] font-semibold text-slate-700">Briefing time</span>
        </div>
        <Input type="time" value={data.briefingTime} onChange={(e) => setTime(e.target.value)} className="h-9 text-[12.5px] max-w-[140px]" data-testid="onboarding-briefing-time" />
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {["07:30", "08:00", "09:00"].map((t) => (
            <button key={t} type="button" onClick={() => setTime(t)} className={cn("text-[10.5px] rounded-full px-2 py-0.5 ring-1 font-medium transition-colors", data.briefingTime === t ? "bg-indigo-50 text-indigo-700 ring-indigo-200" : "bg-white text-slate-600 ring-slate-200")} data-testid={`onboarding-time-${t}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {[
          { key: "push", Icon: Smartphone, label: "Push notification" },
          { key: "email", Icon: Mail, label: "Email" },
          { key: "whatsapp", Icon: MessageCircle, label: "WhatsApp message" },
        ].map((m) => (
          <div key={m.key} className="px-3 py-2.5 flex items-center gap-3" data-testid={`onboarding-method-${m.key}`}>
            <m.Icon size={13} className="text-slate-500" />
            <span className="flex-1 text-[12px] text-slate-800 flex items-center gap-2">
              {m.label}
              {m.key === "push" && <ComingInV25 inline title="Push briefing in V2.5" description="Push notifications (iOS APNs + Android FCM) ship with the mobile companion in V2.5. For V2.0 trial, pick Email or WhatsApp for your morning briefing." />}
            </span>
            <Switch checked={data.briefingMethods[m.key]} disabled={m.key === "push"} onCheckedChange={() => toggleMethod(m.key)} data-testid={`onboarding-method-toggle-${m.key}`} />
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-lg bg-gradient-to-br from-indigo-50/60 to-violet-50/40 ring-1 ring-indigo-200 px-4 py-3 text-[13px] text-slate-800 inline-flex items-start gap-2">
        <Bell size={14} className="text-indigo-600 mt-0.5 shrink-0" />
        <span>{name} will send you a 30-second morning briefing every workday at <span className="font-semibold">{formatted}</span> via <span className="font-semibold">{methodLabel}</span>.</span>
      </div>
    </div>
  );
}

// Welcome banner shown on /today after onboarding completion
export function FirstRunWelcomeBanner() {
  const { completed, welcomeBannerDismissed, dismissWelcomeBanner } = useOnboardingStore();
  const { name } = usePersonaStore();
  const { auth } = useAppStore();
  const ownerFirstName = userFirstName(auth?.user);
  useEffect(() => {
    if (completed && !welcomeBannerDismissed) {
      const t = setTimeout(() => dismissWelcomeBanner(), 8000);
      return () => clearTimeout(t);
    }
  }, [completed, welcomeBannerDismissed, dismissWelcomeBanner]);
  if (!completed || welcomeBannerDismissed) return null;
  return (
    <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/70 via-white to-violet-50/40 px-3 sm:px-4 py-3 mb-4 flex items-start gap-3 min-w-0" data-testid="first-run-welcome">
      <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center shrink-0"><Sparkles size={15} strokeWidth={2.25} /></div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900">All set, {ownerFirstName}.</div>
        <div className="text-[12px] text-slate-600 mt-0.5">Here&apos;s your first briefing. {name} has already started watching your channels.</div>
      </div>
      <button type="button" onClick={dismissWelcomeBanner} className="text-slate-400 hover:text-slate-600 h-8 w-8 inline-flex items-center justify-center shrink-0" data-testid="first-run-welcome-close"><X size={14} /></button>
    </div>
  );
}
